import { Camera, CameraResultType, CameraSource } from '@capacitor/camera';
import type { Photo } from '@capacitor/camera';
import { PlatformUtils } from '@/utils/platform';

/**
 * Represents a captured or selected image with all necessary data
 * for uploading to Firebase Storage
 */
export interface CapturedImage {
  /** Base64 data URL of the image */
  dataUrl: string;
  /** Blob for upload operations */
  blob: Blob;
  /** File object with proper name and MIME type */
  file: File;
  /** Image width in pixels */
  width: number;
  /** Image height in pixels */
  height: number;
  /** Image format (jpeg, png, etc.) */
  format: string;
}

/**
 * Get image dimensions from a data URL
 */
const getImageDimensions = (dataUrl: string): Promise<{ width: number; height: number }> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      resolve({ width: img.width, height: img.height });
    };
    img.onerror = reject;
    img.src = dataUrl;
  });
};

/**
 * Process a Capacitor Photo result into a CapturedImage
 */
const processPhoto = async (photo: Photo): Promise<CapturedImage> => {
  const dataUrl = photo.dataUrl!;

  // Get image dimensions
  const dimensions = await getImageDimensions(dataUrl);

  // Convert data URL to Blob
  const response = await fetch(dataUrl);
  const blob = await response.blob();

  // Create File object with timestamp-based name
  const format = photo.format || 'jpeg';
  const fileName = `camera_${Date.now()}.${format}`;
  const file = new File([blob], fileName, { type: `image/${format}` });

  return {
    dataUrl,
    blob,
    file,
    width: dimensions.width,
    height: dimensions.height,
    format,
  };
};

/**
 * Camera service for capturing and selecting images
 * Works on native platforms (Android/iOS) using Capacitor Camera plugin
 */
export const cameraService = {
  /**
   * Check if camera is available on current platform
   */
  isAvailable: async (): Promise<boolean> => {
    if (!PlatformUtils.isNative()) {
      // On web, check for MediaDevices API
      return !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia);
    }
    return true; // Native platforms always have camera capability
  },

  /**
   * Request camera and photo library permissions
   * Returns true if permissions are granted
   */
  requestPermissions: async (): Promise<boolean> => {
    if (!PlatformUtils.isNative()) {
      // Web: permissions are requested when camera is accessed
      return true;
    }

    try {
      const permissions = await Camera.requestPermissions({
        permissions: ['camera', 'photos'],
      });

      return permissions.camera === 'granted';
    } catch {
      return false;
    }
  },

  /**
   * Check current permission status
   */
  checkPermissions: async (): Promise<{ camera: string; photos: string }> => {
    if (!PlatformUtils.isNative()) {
      return { camera: 'granted', photos: 'granted' };
    }

    try {
      const permissions = await Camera.checkPermissions();
      return {
        camera: permissions.camera,
        photos: permissions.photos,
      };
    } catch {
      return { camera: 'denied', photos: 'denied' };
    }
  },

  /**
   * Take a photo using the device camera
   */
  takePhoto: async (options?: {
    quality?: number;
    width?: number;
    height?: number;
  }): Promise<CapturedImage> => {
    const photo = await Camera.getPhoto({
      quality: options?.quality ?? 90,
      allowEditing: false,
      resultType: CameraResultType.DataUrl,
      source: CameraSource.Camera,
      width: options?.width,
      height: options?.height,
      correctOrientation: true,
    });

    return processPhoto(photo);
  },

  /**
   * Select a photo from the device gallery/photo library
   */
  pickFromGallery: async (options?: {
    quality?: number;
    width?: number;
    height?: number;
  }): Promise<CapturedImage> => {
    const photo = await Camera.getPhoto({
      quality: options?.quality ?? 90,
      allowEditing: false,
      resultType: CameraResultType.DataUrl,
      source: CameraSource.Photos,
      width: options?.width,
      height: options?.height,
      correctOrientation: true,
    });

    return processPhoto(photo);
  },

  /**
   * Show a native prompt to choose between camera or gallery
   * On iOS/Android this shows a native action sheet
   */
  promptForImage: async (options?: {
    quality?: number;
    width?: number;
    height?: number;
  }): Promise<CapturedImage> => {
    const photo = await Camera.getPhoto({
      quality: options?.quality ?? 90,
      allowEditing: false,
      resultType: CameraResultType.DataUrl,
      source: CameraSource.Prompt,
      width: options?.width,
      height: options?.height,
      correctOrientation: true,
      promptLabelHeader: 'Add Image',
      promptLabelCancel: 'Cancel',
      promptLabelPhoto: 'Choose from Gallery',
      promptLabelPicture: 'Take Photo',
    });

    return processPhoto(photo);
  },
};
